/**
 * Compiles tenant **override grants** from org settings.
 *
 * Does not contain default role grants — see permissionIds.ts.
 * Empty `overrideGrants` means all defaults apply; it is not deny-all.
 *
 * To add a tenant-configurable permission: Organization field → row in
 * orgPolicyOverrideRules.ts → Site Settings control. New Organization fields
 * are visible to `when()` automatically; identity fields are not.
 * See docs/authz.md How-to and Reference.
 */
import type { OrgLike } from "@features/plugins/orgPlugins";
import type { Organization } from "@models/Organization";
import type { UserRole } from "@models/UserRole";

import { ORG_POLICY_OVERRIDE_RULES } from "./orgPolicyOverrideRules";
import type { PermissionId } from "./permissionIds";

/**
 * Org snapshot for compile; full session org in production, partials OK in tests.
 * Identity fields are omitted so override rules cannot branch on them.
 */
export type OrgPolicyInput = Partial<
  Omit<Organization, "id" | "name" | "orgCode">
>;

export type OrgPolicyOverrideRule = {
  permissionId: PermissionId;
  when: (org: OrgPolicyInput) => boolean;
  roles: readonly UserRole[];
};

export type AuthZOrganization = OrgLike & Partial<Organization>;

export type OrgPolicy = {
  /** Replace-default grant lists for permissions whose override rules fired. */
  overrideGrants: Partial<Record<PermissionId, readonly UserRole[]>>;
};

/** Shared sentinel — frozen so a caller cannot pollute defaults process-wide. */
export const EMPTY_ORG_POLICY: OrgPolicy = Object.freeze({
  overrideGrants: Object.freeze({}),
});

/**
 * Content fingerprint of fired overrides. Stable across org object identity
 * churn (structuralSharing is disabled on current-user), so AuthZProvider can
 * memoize on this string rather than the organization reference.
 */
export function orgPolicyKey(policy: OrgPolicy): string {
  const ids = Object.keys(policy.overrideGrants) as PermissionId[];
  if (ids.length === 0) return "defaults";
  return ids
    .sort()
    .map((id) => {
      const roles = [...(policy.overrideGrants[id] ?? [])].sort().join(",");
      return `${id}:${roles}`;
    })
    .join("|");
}

export function applyOrgPolicyOverrideRules(
  org: OrgPolicyInput,
  rules: readonly OrgPolicyOverrideRule[]
): OrgPolicy {
  const overrideGrants: Partial<Record<PermissionId, readonly UserRole[]>> = {};
  for (const rule of rules) {
    if (rule.when(org)) {
      overrideGrants[rule.permissionId] = rule.roles;
    }
  }
  if (Object.keys(overrideGrants).length === 0) {
    return EMPTY_ORG_POLICY;
  }
  return { overrideGrants };
}

export function compileOrgPolicy(org: OrgPolicyInput): OrgPolicy {
  return applyOrgPolicyOverrideRules(org, ORG_POLICY_OVERRIDE_RULES);
}
